import { FetchError } from "@medusajs/js-sdk"
import { FindParams, HttpTypes, PaginatedResponse } from "@medusajs/types"
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { fetchQuery, sdk } from "../../lib/client"
import { queryClient } from "../../lib/query-client"
import { queryKeysFactory } from "../../lib/query-key-factory"
import { productsQueryKeys } from "./products"
import {
  processItems,
  createFieldExtractors,
  BaseFilterConfig,
} from "../../utils/client-side-filtering"

const COLLECTION_QUERY_KEY = "collections" as const
export const collectionsQueryKeys = queryKeysFactory(COLLECTION_QUERY_KEY)

export const useCollection = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      { product_collection: HttpTypes.AdminCollection },
      FetchError,
      { product_collection: HttpTypes.AdminCollection },
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: collectionsQueryKeys.detail(id),
    queryFn: async () =>
      await fetchQuery(`/vendor/product-collections/${id}`, {
        method: "GET",
        query: { fields: "*products" },
      }),
    ...options,
  })

  return { ...data, ...rest }
}

export const useCollections = (
  query?: FindParams & HttpTypes.AdminCollectionListParams,
  options?: Omit<
    UseQueryOptions<
      PaginatedResponse<{
        product_collections: HttpTypes.AdminCollection[]
      }>,
      FetchError,
      PaginatedResponse<{
        product_collections: HttpTypes.AdminCollection[]
      }>,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >,
  id?: string,
  filters?: BaseFilterConfig
) => {
  const { data, ...rest } = useQuery({
    queryKey: collectionsQueryKeys.list(query),
    queryFn: async () =>
      fetchQuery("/vendor/product-collections", {
        method: "GET",
        query: { limit: 1000, fields: "*products" }, // Load all for client-side filtering
      }),
    ...options,
  })

  // Define searchable fields for collections
  const searchableFields = {
    title: createFieldExtractors.string("title"),
    handle: createFieldExtractors.string("handle"),
  }

  // Define sortable fields for collections
  const sortableFields = {
    title: (item: any) => item?.title,
    handle: (item: any) => item?.handle,
    created_at: (item: any) => item?.created_at,
    updated_at: (item: any) => item?.updated_at,
  }

  let product_collections = data?.product_collections || []

  // Apply client-side filtering if filters are provided
  if (filters && (filters.q || filters.order)) {
    product_collections = processItems(
      product_collections,
      filters,
      searchableFields,
      sortableFields
    )
  }

  if (id) {
    return {
      ...rest,
      ...data,
      product_collections: product_collections.find((item) => item.id === id),
    }
  }

  return {
    ...data,
    product_collections,
    count: product_collections.length,
    ...rest,
  }
}

export const useUpdateCollection = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminCollectionResponse,
    FetchError,
    HttpTypes.AdminUpdateCollection
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      fetchQuery(`/vendor/product-collections/${id}`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.detail(id),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateCollectionProducts = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminCollectionResponse,
    FetchError,
    HttpTypes.AdminUpdateCollectionProducts
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.admin.productCollection.updateProducts(id, payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.detail(id),
      })
      /**
       * Invalidate products list query to ensure that the products collections are updated.
       */
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useCreateCollection = (
  options?: UseMutationOptions<
    { request: { id?: string } },
    FetchError,
    {
      title: string
      handle?: string
      product_ids?: string[]
      metadata?: Record<string, unknown>
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      fetchQuery(`/vendor/requests`, {
        method: "POST",
        body: {
          request: {
            type: "product_collection",
            data: {
              title: payload.title,
              handle: payload.handle,
              product_ids: payload.product_ids ?? [],
              metadata: payload.metadata,
            },
          },
        },
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteCollection = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminCollectionDeleteResponse,
    FetchError,
    void
  >
) => {
  return useMutation({
    mutationFn: () => sdk.admin.productCollection.delete(id),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.detail(id),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}
